import {
  Button,
  Checkbox,
  Box,
  Flex,
  InputGroup,
  InputLeftAddon,
  CModal,
  CModalBody,
  ModalCloseButton,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@televet/kibble-ui';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import React, { useState } from 'react';
import {
  ClinicPhoneNumberCreateInput,
  GetClinicPhoneNumbersQuery,
  PhoneNumberStatus,
  ServiceCapability,
  ServiceProvider,
  useCreateOneClinicPhoneNumberMutation,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { cleanPhoneNumber, validatePhoneNumber } from 'shared/utils';
import { ApolloQueryResult } from '@apollo/client';

interface IAddClinicPhoneNumberButtonProps {
  totalNumbers: number;
  onAddNumberError: () => void;
  refetchPhoneNumbers: () => Promise<ApolloQueryResult<GetClinicPhoneNumbersQuery>>;
  isClinicDataComplete: boolean;
}

const AddBandwidthClinicPhoneNumberButton = ({
  totalNumbers,
  onAddNumberError,
  refetchPhoneNumbers,
  isClinicDataComplete,
}: IAddClinicPhoneNumberButtonProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [createClinicPhoneNumberErrorMessage, setCreateClinicPhoneNumberErrorMessage] = useState('');
  const [numberToCreate, setNumberToCreate] = useState('');
  const [isBandwidthNumber, setIsBandwidthNumber] = useState(false);

  const handleBandwidthToggle = (): void => {
    setIsBandwidthNumber((isBandwidthNumber) => !isBandwidthNumber);
  };

  const [createOneClinicPhoneNumber] = useCreateOneClinicPhoneNumberMutation();

  const handleAddClinicPhoneNumberClick = (): void => {
    onOpen();
  };

  const resetState = (): void => {
    setCreateClinicPhoneNumberErrorMessage('');
    setNumberToCreate('');
  };

  const handleCreateClinicPhoneNumber = async (): Promise<void> => {
    const phoneValidation = validatePhoneNumber(numberToCreate) && cleanPhoneNumber(numberToCreate).length === 10;
    try {
      if (!phoneValidation) {
        setCreateClinicPhoneNumberErrorMessage('Please enter a valid phone number');
        return;
      }
      const data: ClinicPhoneNumberCreateInput = {
        clinic: { connect: { id: currentClinicId || '' } },
        number: `+1${cleanPhoneNumber(numberToCreate)}`,
        provider: ServiceProvider.Bandwidth,
        capability: ServiceCapability.Sms,
        status: PhoneNumberStatus.New,
        isPrimary: false,
      };

      if (isBandwidthNumber) {
        data.status = PhoneNumberStatus.Hosted;
        data.incomingCarrier = 'Bandwidth Purchased';
        data.isVerified = true;
      }
      await createOneClinicPhoneNumber({
        variables: {
          data,
        },
      });
      await refetchPhoneNumbers();
      resetState();
      onClose();
    } catch {
      setCreateClinicPhoneNumberErrorMessage(
        'Unable to create phone number, probably phone number with SMS capability already exists, please create a Zendesk ticket',
      );
    }
  };
  return (
    <div>
      <Button
        size="sm"
        variant={'secondary'}
        iconName={'plus'}
        onClick={(): void => {
          if (totalNumbers >= 2 || !isClinicDataComplete) {
            onAddNumberError();
          } else {
            handleAddClinicPhoneNumberClick();
          }
        }}
      >
        Add New Number
      </Button>
      <CModal
        isOpen={isOpen}
        onClose={(): void => {
          setCreateClinicPhoneNumberErrorMessage('');
          resetState();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <CModalHeader>Add Number</CModalHeader>
          <ModalCloseButton />
          <CModalBody>
            Please add a valid phone number (123)-456-7890
            <Flex justifyContent={'center'} marginTop="20px">
              <InputGroup width={'200px'}>
                <InputLeftAddon>+1</InputLeftAddon>
                <TextInput
                  type="tel"
                  value={numberToCreate}
                  onChange={(event): void => {
                    setNumberToCreate(event.currentTarget.value);
                  }}
                  placeholder="phone number"
                />
              </InputGroup>
            </Flex>
            <Checkbox mt={4} isChecked={isBandwidthNumber} onChange={handleBandwidthToggle}>
              This number was purchased via Bandwidth
            </Checkbox>
            {createClinicPhoneNumberErrorMessage && (
              <Box marginTop={'10px'} color="text.danger">
                {createClinicPhoneNumberErrorMessage}
              </Box>
            )}
          </CModalBody>
          <CModalFooter marginBottom={'8px'}>
            <Button onClick={handleCreateClinicPhoneNumber}>Add Number</Button>
          </CModalFooter>
        </ModalContent>
      </CModal>
    </div>
  );
};

export default AddBandwidthClinicPhoneNumberButton;
